/**
 * Záložka Přehledy: rozcestník, zakrmení na zimu, zimování, hlášení počtu
 * včelstev k 1. září a výroční zpráva k tisku.
 */
import { useState } from 'react';
import type { Apiary, BeekeeperProfile, Colony, ColonyEvent, Queen } from '../../db/model';
import { getSetting, read, setSetting } from '../../db/db';
import { fmt, naturalLabel } from '../../db/queries';
import {
  DEFAULT_FEED_TARGET,
  FEED_TARGET_SETTING,
  apiaryAt,
  coloniesByApiaryAt,
  seasonCalendar,
  statusAt,
  winterFeeding,
  winterFeedingPeriod,
  wintering,
  winterYears,
} from '../../db/reports';
import { todayISO } from '../../db/rules';
import { season, seasonOf } from '../../db/season';
import { seasonsWithData, yearStats } from '../../db/yearly';
import { useQuery, useSeasonStart } from '../hooks';
import { ExportButtons } from '../export';
import { colonyName, COLONY_STATUS, VARROA_METHOD, fmtDate, plural } from '../labels';
import { CalendarLegend, SeasonCalendar } from '../charts';
import { ApiaryVarroa } from './yearly';
import { Chips, Empty, Link, Notice, Screen, Section, Stepper, toast } from '../ui';
import { confirmSmall } from '../confirm';
import { Icon, type IconName } from '../icons';

const live = <T extends { deletedAt?: string | null }>(xs: T[]) => xs.filter((x) => !x.deletedAt);

function useAll() {
  return useQuery(async () => {
    const [apiaries, colonies, events, queens, profile] = await Promise.all([
      read.all('apiaries'),
      read.all('colonies'),
      read.all('events'),
      read.all('queens'),
      getSetting<BeekeeperProfile>('profile'),
    ]);
    return { apiaries: live(apiaries), colonies: live(colonies), events: live(events), queens: live(queens), profile };
  }, []);
}

const byName = (a: Apiary, b: Apiary) => (a.name ?? '').localeCompare(b.name ?? '', 'cs', { numeric: true });

function YearChips({ years, value, onChange, label = (y: number) => String(y) }: { years: number[]; value: number; onChange: (y: number) => void; label?: (y: number) => string }) {
  if (years.length < 2) return null;
  return (
    <div className="no-print">
      <Chips ariaLabel="Rok" options={years.map((y) => [y, label(y)] as [number, string])} value={value} onChange={(v) => v != null && onChange(v)} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Rozcestník
// ---------------------------------------------------------------------------

export function ReportsHub() {
  const data = useAll();
  const today = todayISO();
  const y = Number(today.slice(0, 4));
  const apiaries = (data?.apiaries ?? []).filter((a) => !a.archived).sort(byName);
  type Item = [string, IconName, string, string];
  const groups: [string, Item[]][] = [
    [
      'Během sezóny',
      [
        ['varroaza', 'varroa', 'Varroáza – grafy', 'Spad roztočů všech včelstev s vyznačeným léčením.'],
        ['kalendar', 'calendar', 'Kalendář sezóny', 'Medníky, medobraní, léčení, krmení a prohlídky po včelstvech.'],
        ['zakrmeni', 'feeding', 'Zakrmení na zimu', 'Kolik kg cukru včelstva dostala a kolik ještě chybí.'],
      ],
    ],
    [
      'Na konci roku',
      [
        ['zimovani', 'snow', 'Zimování', 'Do zimy, přezimovalo, ztráty a podzimní spad.'],
        ['zprava', 'document', 'Výroční zpráva', 'Celý rok na jedné stránce k tisku nebo do PDF.'],
      ],
    ],
    [
      'Úřední',
      [
        ['hlaseni', 'office', 'Hlášení počtu včelstev', `Počty včelstev na stanovištích k 1. 9. ${y}.`],
        ['evidence', 'clipboard', 'Evidence léčiv', 'Záznamy o použití léčiv za kalendářní rok.'],
      ],
    ],
  ];
  return (
    <Screen title="Přehledy" backTo={false}>
      {groups.map(([title, items]) => (
        <Section key={title} title={title}>
          <ul className="cards">
            {items.map(([to, icon, name, desc]) => (
              <li key={to}>
                <Link to={to} className="card report-card">
                  <span className="report-icon" aria-hidden>
                    <Icon name={icon} size={30} />
                  </span>
                  <span>
                    <strong>{name}</strong>
                    <span className="muted small">{desc}</span>
                  </span>
                  <span className="chev">›</span>
                </Link>
              </li>
            ))}
          </ul>
        </Section>
      ))}
      {apiaries.length > 0 && (
        <Section title="Roční přehledy stanovišť">
          <p className="muted small">Med, varroáza všech včelstev, kalendář sezóny a porovnání let.</p>
          <ul className="list">
            {apiaries.map((a) => (
              <li key={a.id}>
                <Link to={`stanoviste/${a.id}/rok`} className="list-link">
                  {a.name} <span className="chev-inline">›</span>
                </Link>
              </li>
            ))}
          </ul>
        </Section>
      )}
    </Screen>
  );
}

// ---------------------------------------------------------------------------
// Zakrmení na zimu
// ---------------------------------------------------------------------------

export function FeedingScreen() {
  const data = useAll();
  const target = useQuery(async () => (await getSetting<number>(FEED_TARGET_SETTING)) ?? DEFAULT_FEED_TARGET, []);
  const today = todayISO();
  const thisYear = Number(today.slice(0, 4));
  const [year, setYear] = useState(thisYear);
  if (!data || target == null) return null;
  const { apiaries, colonies, events } = data;
  const years = [...new Set([thisYear, ...events.filter((e) => e.type === 'feeding' && e.date.slice(5) >= '07-01').map((e) => Number(e.date.slice(0, 4)))])].sort((a, b) => b - a);
  const p = winterFeedingPeriod(year);
  // včelstva, která v tom roce šla do zimy (k 1. 11., letos k dnešku)
  const at = year === thisYear && today < `${year}-11-01` ? today : `${year}-11-01`;
  const alive = colonies.filter((c) => statusAt(c, events, at) === 'active');
  const rows = winterFeeding(alive, events, year);
  const apOf = new Map(rows.map((r) => [r.colony.id, apiaryAt(r.colony, events, at)]));
  const groups = apiaries
    .sort(byName)
    .map((a) => ({ a, rows: rows.filter((r) => apOf.get(r.colony.id) === a.id).sort((x, y) => naturalLabel(x.colony, y.colony)) }))
    .filter((g) => g.rows.length);
  const missing = rows.reduce((s, r) => s + Math.max(0, target - r.sugarKg), 0);
  const done = rows.filter((r) => r.sugarKg >= target).length;
  const total = rows.reduce((s, r) => s + r.sugarKg, 0);

  return (
    <Screen title="Zakrmení na zimu" subtitle={`Krmení od ${fmtDate(p.from)} v kg cukru`} backTo="prehledy">
      <YearChips years={years} value={year} onChange={setYear} />
      <div className="row two no-print">
        <div className="field">
          <span className="field-label">Cíl na včelstvo</span>
          <Stepper
            ariaLabel="Cíl kg cukru na včelstvo"
            value={target}
            step={0.5}
            min={1}
            max={40}
            unit="kg"
            onChange={async (v) => {
              if (v && v > 0) {
                await setSetting(FEED_TARGET_SETTING, v);
                confirmSmall('Cíl uložen');
              }
            }}
          />
        </div>
      </div>
      {rows.length === 0 ? (
        <Empty>V tomto roce nejsou žádná živá včelstva.</Empty>
      ) : (
        <>
          <div className="info-grid">
            <div className="info">
              <div className="info-label">Zakrmeno</div>
              <div className="info-value">
                {done} z {plural(rows.length, 'včelstva', 'včelstev', 'včelstev')}
                <div className="muted small">má aspoň {fmt(target)} kg cukru</div>
              </div>
            </div>
            <div className={'info' + (missing > 0 ? ' warn-tile' : '')}>
              <div className="info-label">Chybí cukru</div>
              <div className={'info-value' + (missing > 0 ? ' alarm' : '')}>
                {fmt(missing)} kg
                <div className="muted small">celkem dáno {fmt(total)} kg</div>
              </div>
            </div>
          </div>
          {groups.map(({ a, rows }) => (
            <Section key={a.id} title={a.name}>
              <ul className="list feed-list">
                {rows.map((r) => {
                  const pct = Math.min(100, (r.sugarKg / target) * 100);
                  const left = Math.max(0, target - r.sugarKg);
                  return (
                    <li key={r.colony.id}>
                      <Link to={`vcelstvo/${r.colony.id}/historie?filtr=feeding`} className="feed-row">
                        <span className="feed-label">{colonyName(r.colony.label)}</span>
                        <span className="feed-bar" aria-hidden>
                          <span className={'feed-fill' + (left === 0 ? ' ok' : '')} style={{ width: `${pct}%` }} />
                        </span>
                        <span className="feed-val">
                          <span className="nowrap">
                            <strong>{fmt(r.sugarKg)}</strong> / {fmt(target)} kg
                          </span>
                          <span className={'small ' + (left === 0 ? 'ok-text' : 'alarm-text')}>{left === 0 ? 'hotovo' : `chybí ${fmt(left)} kg`}</span>
                        </span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </Section>
          ))}
          <p className="small muted">
            Počítá se krmení zapsané od 1. 7. do konce roku, přepočtené na kg cukru podle druhu krmiva. Cíl si nastavte podle
            síly včelstev a zásob v úlu.
          </p>
        </>
      )}
      <ExportButtons
        table={{
          name: `Zakrmení ${year}`,
          head: ['Stanoviště', 'Včelstvo', 'Cukr kg', 'Cíl kg', 'Chybí kg', 'Krmení (×)', 'Naposledy'],
          rows: groups.flatMap(({ a, rows }) =>
            rows.map((r) => [a.name, colonyName(r.colony.label), r.sugarKg, target, Math.round(Math.max(0, target - r.sugarKg) * 10) / 10, r.feedings, r.lastDate ? fmtDate(r.lastDate) : '']),
          ),
        }}
      />
    </Screen>
  );
}

// ---------------------------------------------------------------------------
// Zimování
// ---------------------------------------------------------------------------

export function WinteringScreen() {
  const data = useAll();
  if (!data) return null;
  const { apiaries, colonies, events } = data;
  const today = todayISO();
  const years = winterYears(colonies, events, today);
  const winters = years.map((y) => wintering(colonies, events, y, today)).filter((w) => w.colonies.length);
  const apName = (id: string) => apiaries.find((a) => a.id === id)?.name ?? '?';
  const avg = (xs: (number | null)[]) => {
    const v = xs.filter((x): x is number => x != null);
    return v.length ? v.reduce((a, b) => a + b, 0) / v.length : null;
  };

  return (
    <Screen title="Zimování" subtitle="Do zimy k 1. 11., přezimovalo k 1. 4." backTo="prehledy">
      {winters.length === 0 && <Empty>Zatím není žádná zima s daty.</Empty>}
      {winters.length > 1 && (
        <Section title="Ztráty po zimách">
          <div className="table-wrap">
            <table className="log compare">
              <thead>
                <tr>
                  <th>Zima</th>
                  <th>Do zimy</th>
                  <th>Přezimovalo</th>
                  <th>Ztráty</th>
                </tr>
              </thead>
              <tbody>
                {winters.map((w) => {
                  const lost = w.colonies.filter((c) => c.outcome !== 'active').length;
                  return (
                    <tr key={w.year}>
                      <th scope="row">
                        {w.label}
                        {w.ongoing && <div className="small muted">probíhá</div>}
                      </th>
                      <td>{w.colonies.length}</td>
                      <td>{w.colonies.length - lost}</td>
                      <td className={lost ? 'alarm' : ''}>
                        {lost} ({fmt((lost / w.colonies.length) * 100, 0)} %)
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Section>
      )}
      {winters.map((w) => {
        const lostList = w.colonies.filter((c) => c.outcome !== 'active');
        const survived = w.colonies.filter((c) => c.outcome === 'active');
        const byAp = [...new Set(w.colonies.map((c) => c.apiaryId))];
        const mS = avg(survived.map((c) => c.autumnMites));
        const mL = avg(lostList.map((c) => c.autumnMites));
        return (
          <Section key={w.year} title={`Zima ${w.label}${w.ongoing ? ' – probíhá' : ''}`}>
            <div className="info-grid">
              <div className="info">
                <div className="info-label">Do zimy</div>
                <div className="info-value">{w.colonies.length}</div>
              </div>
              <div className="info">
                <div className="info-label">{w.ongoing ? 'Zatím ztráty' : 'Ztráty'}</div>
                <div className={'info-value' + (lostList.length ? ' alarm' : '')}>
                  {lostList.length}
                  <div className="muted small">{fmt((lostList.length / w.colonies.length) * 100, 0)} %</div>
                </div>
              </div>
            </div>
            {byAp.length > 1 && (
              <ul className="plain rows">
                {byAp.map((id) => {
                  const cs = w.colonies.filter((c) => c.apiaryId === id);
                  const l = cs.filter((c) => c.outcome !== 'active').length;
                  return (
                    <li key={id}>
                      <span className="grow">{apName(id)}</span>
                      <span>
                        {cs.length - l} z {cs.length}
                        {l > 0 && <span className="alarm-text"> · −{l}</span>}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
            {lostList.length > 0 && (
              <>
                <h3 className="sub">Ztracená včelstva</h3>
                <ul className="plain rows">
                  {lostList.map((c) => {
                    const ch = events
                      .filter((e): e is Extract<ColonyEvent, { type: 'status_change' }> => e.type === 'status_change' && e.colonyIds.includes(c.colony.id))
                      .sort((a, b) => b.date.localeCompare(a.date))[0];
                    return (
                      <li key={c.colony.id}>
                        <Link to={`vcelstvo/${c.colony.id}`} className="grow">
                          {colonyName(c.colony.label)} <span className="muted small">({apName(c.apiaryId)})</span>
                        </Link>
                        <span className="small">
                          {COLONY_STATUS[c.outcome ?? 'dead']}
                          {ch ? ` ${fmtDate(ch.date)}` : ''}
                          {ch?.cause ? ` – ${ch.cause}` : ''}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
            {(mS != null || mL != null) && (
              <p className="small muted">
                Průměrný podzimní spad (srpen–říjen): přezimovala {mS != null ? plural(Math.round(mS), 'roztoč', 'roztoči', 'roztočů') : '–'}
                {lostList.length > 0 && <>, ztracená {mL != null ? plural(Math.round(mL), 'roztoč', 'roztoči', 'roztočů') : '–'}</>}.
              </p>
            )}
          </Section>
        );
      })}
      <ExportButtons
        table={{
          name: 'Zimování',
          head: ['Zima', 'Stanoviště', 'Včelstvo', 'Výsledek', 'Podzimní spad roztočů'],
          rows: winters.flatMap((w) =>
            w.colonies.map((c) => [
              w.label,
              apName(c.apiaryId),
              colonyName(c.colony.label),
              c.outcome === 'active' ? (w.ongoing ? 'zatím žije' : 'přezimovalo') : COLONY_STATUS[c.outcome ?? 'dead'],
              c.autumnMites,
            ]),
          ),
        }}
      />
    </Screen>
  );
}

// ---------------------------------------------------------------------------
// Hlášení počtu včelstev k 1. září
// ---------------------------------------------------------------------------

export function CountReportScreen() {
  const data = useAll();
  const thisYear = Number(todayISO().slice(0, 4));
  const [year, setYear] = useState(thisYear);
  if (!data) return null;
  const { apiaries, colonies, events, profile } = data;
  const firstYear = Math.min(thisYear, ...events.map((e) => Number(e.date.slice(0, 4))));
  const years: number[] = [];
  for (let y = thisYear; y >= firstYear && years.length < 6; y--) years.push(y);
  const date = `${year}-09-01`;
  const rows = coloniesByApiaryAt(apiaries, colonies, events, date).sort((a, b) => byName(a.apiary, b.apiary));
  const total = rows.reduce((s, r) => s + r.colonies.length, 0);
  const meal = events
    .filter((e): e is Extract<ColonyEvent, { type: 'varroa_check' }> => e.type === 'varroa_check' && !!e.sentToLab && e.date.startsWith(String(year)))
    .sort((a, b) => a.date.localeCompare(b.date));
  const future = date > todayISO();

  return (
    <Screen title="Hlášení počtu včelstev" subtitle={`Stav k 1. 9. ${year}`} backTo="prehledy">
      <YearChips years={years} value={year} onChange={setYear} />
      <div className="print-only print-head">
        <h1>Počet včelstev a stanoviště k 1. 9. {year}</h1>
        {profile?.name && (
          <p>
            Chovatel: {profile.name}
            {profile.registrationNumber ? `, reg. č. ${profile.registrationNumber}` : ''}
            {profile.address ? `, ${profile.address}` : ''}
          </p>
        )}
      </div>
      {future && <Notice tone="info">1. 9. {year} ještě nenastal – čísla ukazují včelstva, která žijí dnes.</Notice>}
      <Notice tone="info">
        Chovatel hlásí počet včelstev a umístění stanovišť <strong>k 1. září, nejpozději do 15. září</strong>.{' '}
        <a href="https://www.portalvcelarstvi.cz/" target="_blank" rel="noopener noreferrer" className="no-print">
          Podat hlášení na Portálu včelaře (portalvcelarstvi.cz) ↗
        </a>
      </Notice>
      {rows.length === 0 ? (
        <Empty>K tomuto dni nejsou evidována žádná živá včelstva.</Empty>
      ) : (
        <div className="table-wrap">
          <table className="log">
            <thead>
              <tr>
                <th>Stanoviště</th>
                <th>Reg. číslo</th>
                <th>Katastr / obec</th>
                <th>Včelstev</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.apiary.id}>
                  <td>
                    {r.apiary.name}
                    {r.apiary.location && (
                      <div className="small muted">
                        GPS {r.apiary.location.lat.toFixed(5)}, {r.apiary.location.lng.toFixed(5)}
                      </div>
                    )}
                  </td>
                  <td>{r.apiary.registrationNumber || <span className="alarm-text">doplnit</span>}</td>
                  <td>{r.apiary.cadastralArea || '–'}</td>
                  <td>
                    <strong>{r.colonies.length}</strong>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th colSpan={3}>Celkem</th>
                <th>{total}</th>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
      {rows.some((r) => !r.apiary.registrationNumber || !r.apiary.cadastralArea) && (
        <p className="small muted no-print">Registrační číslo a katastr doplníte v úpravě stanoviště.</p>
      )}
      {!profile?.registrationNumber && <p className="small muted no-print">Své registrační číslo chovatele doplňte v Nastavení → Údaje chovatele.</p>}

      <Section title={`Zimní měl odeslaná k vyšetření (${year})`}>
        {meal.length === 0 ? (
          <p className="muted small">V tomto roce není zapsaná žádná odeslaná měl (zaškrtnutí „Zimní měl odeslána k vyšetření“ u kontroly varroázy).</p>
        ) : (
          <ul className="plain rows">
            {meal.map((e) => (
              <li key={e.id}>
                <span className="row-date">{fmtDate(e.date)}</span>
                <span>
                  {apiaries.find((a) => a.id === e.apiaryId)?.name} – včelstvo {colonyName(colonies.find((c) => c.id === e.colonyIds[0])?.label)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Section>
      <ExportButtons
        table={{
          name: `Počet včelstev k 1. 9. ${year}`,
          head: ['Stanoviště', 'Registrační číslo', 'Katastr / obec', 'GPS', 'Počet včelstev', 'Včelstva'],
          rows: [
            ...rows.map((r) => [
              r.apiary.name,
              r.apiary.registrationNumber,
              r.apiary.cadastralArea,
              r.apiary.location ? `${r.apiary.location.lat.toFixed(5)}, ${r.apiary.location.lng.toFixed(5)}` : '',
              r.colonies.length,
              [...r.colonies].sort(naturalLabel).map((c) => colonyName(c.label)).join(', '),
            ]),
            ['Celkem', '', '', '', total, ''],
          ],
        }}
      />
    </Screen>
  );
}

// ---------------------------------------------------------------------------
// Výroční zpráva
// ---------------------------------------------------------------------------

export function AnnualReportScreen() {
  const data = useAll();
  const startMonth = useSeasonStart();
  const [picked, setPicked] = useState<number | null>(null);
  if (!data || startMonth == null) return null;
  const { apiaries, colonies, events, queens, profile } = data;
  const today = todayISO();
  const years = seasonsWithData(events, startMonth, today);
  const year = picked ?? years[0] ?? seasonOf(today, startMonth).year;
  const s = season(year, startMonth);
  const end = s.to < today ? s.to : today;
  const report = buildAnnual(apiaries, colonies, events, queens, s.from, end, (c) => yearStats(c, events, queens, s));
  const prevYear = years.includes(year - 1) ? year - 1 : null;
  const prev = prevYear != null ? (() => {
    const ps = season(prevYear, startMonth);
    return buildAnnual(apiaries, colonies, events, queens, ps.from, ps.to, (c) => yearStats(c, events, queens, ps));
  })() : null;
  const delta = (a: number, b: number | undefined, digits = 1, unit = '') => {
    if (b == null) return null;
    const d = a - b;
    if (Math.abs(d) < 0.05) return <span className="muted small"> (stejně jako {season(year - 1, startMonth).label})</span>;
    return (
      <span className="muted small">
        {' '}
        ({d > 0 ? '+' : '−'}
        {fmt(Math.abs(d), digits)}
        {unit} proti {season(year - 1, startMonth).label})
      </span>
    );
  };

  return (
    <Screen title={`Výroční zpráva ${s.label}`} subtitle={`${fmtDate(s.from)} – ${fmtDate(s.to)}`} backTo="prehledy">
      <YearChips years={years} value={year} onChange={setPicked} label={(y) => season(y, startMonth).label} />
      <article className="annual">
        <header className="annual-head">
          <h2>Včelařský rok {s.label}</h2>
          <p className="muted">
            {profile?.name ?? 'Chovatel'}
            {profile?.registrationNumber ? ` · reg. č. ${profile.registrationNumber}` : ''} · {fmtDate(s.from)} – {fmtDate(s.to)}
            {end < s.to ? ` (stav k ${fmtDate(end)})` : ''}
          </p>
        </header>

        <div className="annual-grid">
          <div className="info">
            <div className="info-label">Včelstva</div>
            <div className="info-value">
              {report.start} → {report.end}
              <div className="muted small">na začátku → na konci roku</div>
            </div>
          </div>
          <div className="info">
            <div className="info-label">Med</div>
            <div className="info-value">
              {fmt(report.honey)} kg{delta(report.honey, prev?.honey, 1, ' kg')}
              <div className="muted small">{report.producing ? `${fmt(report.honey / report.producing)} kg na medující včelstvo` : 'bez medobraní'}</div>
            </div>
          </div>
          <div className="info">
            <div className="info-label">Krmení</div>
            <div className="info-value">
              {fmt(report.sugar)} kg cukru
              <div className="muted small">{report.end ? `${fmt(report.sugar / Math.max(1, report.end))} kg na včelstvo` : ''}</div>
            </div>
          </div>
          <div className="info">
            <div className="info-label">Léčení / spad</div>
            <div className="info-value">
              {report.treatments}× · {fmt(report.mites, 0)}
              <div className="muted small">ošetření včelstev · spad roztočů</div>
            </div>
          </div>
          <div className="info">
            <div className="info-label">Přibylo</div>
            <div className="info-value">
              {report.added}
              <div className="muted small">oddělky, roje, nákup</div>
            </div>
          </div>
          <div className={'info'}>
            <div className="info-label">Ubylo</div>
            <div className={'info-value' + (report.losses.length ? ' alarm' : '')}>
              {report.losses.length}
              <div className="muted small">úhyn, spojení, prodej</div>
            </div>
          </div>
        </div>

        {report.byApiary.length > 0 && (
          <section className="annual-sec">
            <h3>Stanoviště</h3>
            <div className="table-wrap">
              <table className="log">
                <thead>
                  <tr>
                    <th>Stanoviště</th>
                    <th>Včelstev</th>
                    <th>Med kg</th>
                    <th>kg/včelstvo</th>
                    <th>Cukr kg</th>
                    <th>Spad</th>
                  </tr>
                </thead>
                <tbody>
                  {report.byApiary.map((r) => (
                    <tr key={r.apiary.id}>
                      <td>{r.apiary.name}</td>
                      <td>{r.count}</td>
                      <td>{fmt(r.honey)}</td>
                      <td>{r.count ? fmt(r.honey / r.count) : '–'}</td>
                      <td>{fmt(r.sugar)}</td>
                      <td>{fmt(r.mites, 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {report.ranked.length > 1 && (
          <section className="annual-sec two-col">
            <div>
              <h3>Nejlepší včelstva</h3>
              <ol className="plain rank">
                {report.ranked.slice(0, 3).map((r) => (
                  <li key={r.colony.id}>
                    <strong>{colonyName(r.colony.label)}</strong> <span className="muted small">{r.apiary}</span> – {fmt(r.honey)} kg
                    {r.queenLine ? <span className="muted small"> · {r.queenLine}</span> : null}
                  </li>
                ))}
              </ol>
            </div>
            <div>
              <h3>Nejslabší včelstva</h3>
              <ol className="plain rank">
                {report.ranked
                  .slice(-3)
                  .reverse()
                  .map((r) => (
                    <li key={r.colony.id}>
                      <strong>{colonyName(r.colony.label)}</strong> <span className="muted small">{r.apiary}</span> – {fmt(r.honey)} kg
                      {r.mites != null ? <span className="muted small"> · spad {r.mites}</span> : null}
                    </li>
                  ))}
              </ol>
            </div>
          </section>
        )}

        {report.losses.length > 0 && (
          <section className="annual-sec">
            <h3>Ztráty</h3>
            <ul className="plain rows">
              {report.losses.map((l, i) => (
                <li key={i}>
                  <span className="row-date">{fmtDate(l.date)}</span>
                  <span>
                    {l.label} ({l.apiary}) – {COLONY_STATUS[l.status]}
                    {l.cause ? `: ${l.cause}` : ''}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}
        <p className="small muted annual-foot">Vytvořeno v aplikaci Úlník {fmtDate(today)}.</p>
      </article>
      <ExportButtons
        table={{
          name: `Výroční zpráva ${s.label}`,
          head: ['Včelstvo', 'Stanoviště', 'Med kg', 'Krmení cukr kg', 'Léčení', 'Celkový spad roztočů', 'Linie matky'],
          rows: report.ranked.map((r) => [colonyName(r.colony.label), r.apiary, r.honey, r.sugar, r.treatments, r.mites, r.queenLine]),
        }}
      />
    </Screen>
  );
}

function buildAnnual(
  apiaries: Apiary[],
  colonies: Colony[],
  events: ColonyEvent[],
  _queens: Queen[],
  from: string,
  end: string,
  stats: (c: Colony) => ReturnType<typeof yearStats>,
) {
  const inRange = events.filter((e) => e.date >= from && e.date <= end);
  const dayBefore = new Date(Date.parse(from) - 86400000).toISOString().slice(0, 10);
  const startSet = colonies.filter((c) => statusAt(c, events, dayBefore) === 'active');
  const endSet = colonies.filter((c) => statusAt(c, events, end) === 'active');
  // včelstva, která v roce existovala
  const present = colonies.filter((c) => statusAt(c, events, end) != null && (startSet.includes(c) || inRange.some((e) => e.colonyIds.includes(c.id)) || endSet.includes(c)));
  const per = present.map((c) => ({ c, st: stats(c), ap: apiaryAt(c, events, end) }));
  const apName = (id: string) => apiaries.find((a) => a.id === id)?.name ?? '';
  const losses = inRange
    .filter((e): e is Extract<ColonyEvent, { type: 'status_change' }> => e.type === 'status_change' && e.newStatus !== 'active')
    .map((e) => {
      const c = colonies.find((x) => x.id === e.colonyIds[0]);
      return { date: e.date, label: colonyName(c?.label) ?? '?', apiary: apName(e.apiaryId), status: e.newStatus, cause: e.cause };
    })
    .sort((a, b) => a.date.localeCompare(b.date));
  const byApiary = apiaries
    .map((a) => {
      const rs = per.filter((p) => p.ap === a.id);
      return {
        apiary: a,
        count: rs.filter((r) => endSet.includes(r.c)).length,
        honey: rs.reduce((s, r) => s + r.st.honeyKg, 0),
        sugar: rs.reduce((s, r) => s + r.st.feedSugarKg, 0),
        mites: rs.reduce((s, r) => s + (r.st.totalMites ?? 0), 0),
        any: rs.length,
      };
    })
    .filter((r) => r.any)
    .sort((a, b) => byName(a.apiary, b.apiary));
  const ranked = per
    .map((p) => ({
      colony: p.c,
      apiary: apName(p.ap),
      honey: p.st.honeyKg,
      sugar: p.st.feedSugarKg,
      treatments: p.st.treatments.length,
      mites: p.st.totalMites,
      queenLine: p.st.queens.at(-1)?.line,
    }))
    .sort((a, b) => b.honey - a.honey || naturalLabel(a.colony, b.colony));
  return {
    start: startSet.length,
    end: endSet.length,
    added: present.filter((c) => !startSet.includes(c)).length,
    honey: per.reduce((s, p) => s + p.st.honeyKg, 0),
    producing: per.filter((p) => p.st.honeyKg > 0).length,
    sugar: per.reduce((s, p) => s + p.st.feedSugarKg, 0),
    treatments: per.reduce((s, p) => s + p.st.treatments.length, 0),
    mites: per.reduce((s, p) => s + (p.st.totalMites ?? 0), 0),
    losses,
    byApiary,
    ranked,
  };
}

// ---------------------------------------------------------------------------
// Varroáza a kalendář sezóny po stanovištích
// ---------------------------------------------------------------------------

function useApiarySeason() {
  const data = useAll();
  const startMonth = useSeasonStart();
  const [apiaryId, setApiaryId] = useState<string | null>(null);
  const [picked, setPicked] = useState<number | null>(null);
  if (!data || startMonth == null) return null;
  const apiaries = data.apiaries
    .filter((a) => !a.archived && (data.colonies.some((c) => c.apiaryId === a.id) || data.events.some((e) => e.apiaryId === a.id)))
    .sort(byName);
  const ap = apiaries.find((a) => a.id === apiaryId) ?? apiaries[0];
  const apEvents = ap ? data.events.filter((e) => e.apiaryId === ap.id) : [];
  const years = seasonsWithData(apEvents, startMonth, todayISO());
  const year = picked != null && years.includes(picked) ? picked : years[0] ?? seasonOf(todayISO(), startMonth).year;
  const s = season(year, startMonth);
  const colonies = ap
    ? data.colonies
        .filter((c) => apEvents.some((e) => e.colonyIds.includes(c.id) && e.date >= s.from && e.date <= s.to) || (c.apiaryId === ap.id && c.status === 'active'))
        .sort(naturalLabel)
    : [];
  const pickers = (
    <div className="no-print pickers">
      {apiaries.length > 1 && (
        <Chips ariaLabel="Stanoviště" options={apiaries.map((a) => [a.id, a.name] as [string, string])} value={ap?.id} onChange={(v) => v && setApiaryId(v)} />
      )}
      {years.length > 1 && (
        <Chips ariaLabel="Rok" options={years.map((y) => [y, season(y, startMonth).label] as [number, string])} value={year} onChange={(v) => v != null && setPicked(v)} />
      )}
    </div>
  );
  return { ...data, ap, apEvents, s, colonies, pickers };
}

export function VarroaOverviewScreen() {
  const d = useApiarySeason();
  if (!d) return null;
  const { ap, apEvents, s, colonies, pickers } = d;
  const checks = apEvents
    .filter((e): e is Extract<ColonyEvent, { type: 'varroa_check' }> => e.type === 'varroa_check' && e.date >= s.from && e.date <= s.to)
    .sort((a, b) => a.date.localeCompare(b.date));
  const label = (id: string) => colonyName(colonies.find((c) => c.id === id)?.label) ?? colonyName(d.colonies.find((c) => c.id === id)?.label) ?? '';
  return (
    <Screen title="Varroáza" subtitle={ap ? `${ap.name} · rok ${s.label}` : undefined} backTo="prehledy">
      {pickers}
      <div className="print-only print-head">
        <h1>
          Varroáza – {ap?.name} – rok {s.label}
        </h1>
      </div>
      {!ap || colonies.length === 0 ? (
        <Empty>Zatím tu nejsou žádná včelstva.</Empty>
      ) : (
        <ApiaryVarroa colonies={colonies} events={apEvents} from={s.from} to={s.to} />
      )}
      {ap && checks.length === 0 && <p className="muted">V tomto roce tu nejsou žádné kontroly varroázy ani léčení.</p>}
      <p className="small muted no-print">Ťuknutím na graf se otevře roční přehled včelstva s velkým grafem a hodnotami.</p>
      <ExportButtons
        table={{
          name: `Varroáza ${ap?.name ?? ''} ${s.label}`,
          head: ['Datum', 'Včelstvo', 'Metoda', 'Roztočů', 'Dní na podložce', 'Vzorek včel', 'Odesláno k vyšetření'],
          rows: checks.map((e) => [fmtDate(e.date), label(e.colonyIds[0]), VARROA_METHOD[e.method], e.miteCount, e.days, e.sampleSize, e.sentToLab ? 'ano' : '']),
        }}
      />
    </Screen>
  );
}

export function SeasonCalendarScreen() {
  const d = useApiarySeason();
  if (!d) return null;
  const { ap, apEvents, s, colonies, pickers } = d;
  const rows = seasonCalendar(colonies, apEvents, s.from, s.to);
  const dates = (xs: string[]) => xs.map(fmtDate).join(', ');
  return (
    <Screen title="Kalendář sezóny" subtitle={ap ? `${ap.name} · rok ${s.label}` : undefined} backTo="prehledy">
      {pickers}
      <div className="print-only print-head">
        <h1>
          Kalendář sezóny – {ap?.name} – rok {s.label}
        </h1>
      </div>
      {rows.length === 0 ? (
        <Empty>Zatím tu nejsou žádná včelstva.</Empty>
      ) : (
        <div className="chart-card">
          <SeasonCalendar rows={rows} from={s.from} to={s.to} />
          <CalendarLegend />
        </div>
      )}
      <p className="small muted no-print">Přesná data všech zásahů najdete v exportu do Excelu.</p>
      <ExportButtons
        table={{
          name: `Kalendář sezóny ${ap?.name ?? ''} ${s.label}`,
          head: ['Včelstvo', 'Medníky', 'Medobraní', 'Léčení', 'Krmení', 'Prohlídky'],
          rows: rows.map((r) => [
            colonyName(r.colony.label),
            r.supers.map((x) => `${x.count}× ${fmtDate(x.from)} – ${fmtDate(x.to)}`).join('; '),
            dates(r.harvests),
            dates(r.treatments),
            dates(r.feedings),
            dates(r.inspections),
          ]),
        }}
      />
    </Screen>
  );
}
